import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiService, FilterSchema } from '../../services/api'
import { Product } from '../../types/product'
import { getMinEffectivePrice } from '../../utils/cartUtils'

// Async thunks
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (params: { limit?: number; offset?: number } = {}) => {
    const { products, schema } = await ApiService.getProductsAndSchema(params)
    return { products, schema }
  },
)

export const fetchProductBySlug = createAsyncThunk(
  'products/fetchProductBySlug',
  async (slug: string) => {
    const product = await ApiService.getProductBySlug(slug)
    if (!product) {
      throw new Error('Product not found')
    }
    return product
  },
)

export const fetchRelatedProducts = createAsyncThunk(
  'products/fetchRelatedProducts',
  async (product: Product) => {
    const allProducts = await ApiService.getProducts({
      category: product.categoryInternalName,
      limit: 20,
    })
    return allProducts
      .filter(
        p => p && p.id !== product.id && p.categoryInternalName === product.categoryInternalName,
      )
      .slice(0, 4)
  },
)

// Types
interface ProductsState {
  // Main products data
  products: Product[]
  filteredProducts: Product[]
  product: Product | null
  relatedProducts: Product[]

  // Categorized products
  newProducts: Product[]
  coffeeProducts: Product[]
  dripProducts: Product[]
  capsuleProducts: Product[]

  // Schema and filters
  filterSchema: FilterSchema | undefined

  // Loading states
  loading: boolean
  loadingProduct: boolean
  loadingRelated: boolean
  loadingNew: boolean
  loadingCoffee: boolean
  loadingDrip: boolean
  loadingCapsule: boolean

  // Error state
  error: string | null

  // Cache timestamps
  lastFetch: number | null
  lastProductFetch: number | null
}

const initialState: ProductsState = {
  products: [],
  filteredProducts: [],
  product: null,
  relatedProducts: [],
  newProducts: [],
  coffeeProducts: [],
  dripProducts: [],
  capsuleProducts: [],
  filterSchema: undefined,
  loading: false,
  loadingProduct: false,
  loadingRelated: false,
  loadingNew: true,
  loadingCoffee: true,
  loadingDrip: true,
  loadingCapsule: true,
  error: null,
  lastFetch: null,
  lastProductFetch: null,
}

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    // Filter products locally
    filterProducts: (
      state,
      action: PayloadAction<{
        category?: string
        beanType?: string
        processingMethod?: string
        originCountry?: string
        minPrice?: number
        maxPrice?: number
        minScaScore?: number
        maxScaScore?: number
        tags?: string[]
      }>,
    ) => {
      const filters = action.payload
      let filtered = [...state.products]

      // Category filter
      if (filters.category) {
        filtered = filtered.filter(p => p.categoryInternalName === filters.category)
      }

      // Bean type filter
      if (filters.beanType) {
        filtered = filtered.filter(p => p.beanType?.value === filters.beanType)
      }

      // Processing method filter
      if (filters.processingMethod) {
        filtered = filtered.filter(p => p.processingMethod?.value === filters.processingMethod)
      }

      // Origin country filter
      if (filters.originCountry) {
        filtered = filtered.filter(p => p.originCountry === filters.originCountry)
      }

      // Price range filter
      if (filters.minPrice !== undefined || filters.maxPrice !== undefined) {
        filtered = filtered.filter(p => {
          const minPrice = getMinEffectivePrice(p.prices || [])
          if (minPrice === null) return false
          if (filters.minPrice !== undefined && minPrice < filters.minPrice) return false
          if (filters.maxPrice !== undefined && minPrice > filters.maxPrice) return false
          return true
        })
      }

      // SCA score range filter
      if (filters.minScaScore !== undefined || filters.maxScaScore !== undefined) {
        filtered = filtered.filter(p => {
          if (typeof p.scaScore !== 'number') return false
          if (filters.minScaScore !== undefined && p.scaScore < filters.minScaScore) return false
          if (filters.maxScaScore !== undefined && p.scaScore > filters.maxScaScore) return false
          return true
        })
      }

      // Tags filter
      if (filters.tags && filters.tags.length > 0) {
        filtered = filtered.filter(p =>
          filters.tags!.some(tag => p.tags.some(productTag => productTag.name === tag)),
        )
      }

      state.filteredProducts = filtered
    },

    // Sort products
    sortProducts: (
      state,
      action: PayloadAction<'popularity' | 'price-asc' | 'price-desc' | 'newest'>,
    ) => {
      const sortOption = action.payload
      const products = [...state.filteredProducts]

      products.sort((a, b) => {
        const priceA = getMinEffectivePrice(a.prices || []) ?? Infinity
        const priceB = getMinEffectivePrice(b.prices || []) ?? Infinity
        switch (sortOption) {
          case 'price-asc':
            return priceA - priceB
          case 'price-desc':
            return priceB - priceA
          case 'newest':
            return new Date(b.roastDate).getTime() - new Date(a.roastDate).getTime()
          case 'popularity':
          default:
            return (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity)
        }
      })

      state.filteredProducts = products
    },

    // Clear current product
    clearProduct: state => {
      state.product = null
      state.relatedProducts = []
    },

    // Clear error
    clearError: state => {
      state.error = null
    },

    // Set categorized products
    setCategorizedProducts: (
      state,
      action: PayloadAction<{
        newProducts: Product[]
        coffeeProducts: Product[]
        dripProducts: Product[]
        capsuleProducts: Product[]
      }>,
    ) => {
      const { newProducts, coffeeProducts, dripProducts, capsuleProducts } = action.payload
      state.newProducts = newProducts
      state.coffeeProducts = coffeeProducts
      state.dripProducts = dripProducts
      state.capsuleProducts = capsuleProducts
      state.loadingNew = false
      state.loadingCoffee = false
      state.loadingDrip = false
      state.loadingCapsule = false
    },
  },
  extraReducers: builder => {
    // Fetch products
    builder
      .addCase(fetchProducts.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false
        
        // Сортування за displayOrder (менше значення = вище в списку)
        const sortedProducts = [...action.payload.products].sort(
          (a, b) => (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity),
        )
        
        state.products = sortedProducts
        state.filteredProducts = sortedProducts
        state.filterSchema = action.payload.schema
        state.lastFetch = Date.now()

        // Auto-categorize products (вже відсортовані за displayOrder)
        const newProducts = sortedProducts
          .filter(p => p.tags.some(tag => tag.name === 'новинка'))
          .slice(0, 6)

        const coffeeProducts = sortedProducts
          .filter(p => p.categoryInternalName === 'kava')
          .slice(0, 6)

        const dripProducts = sortedProducts
          .filter(p => p.categoryInternalName === 'dripi')
          .slice(0, 6)

        const capsuleProducts = sortedProducts
          .filter(p => p.categoryInternalName === 'kapsuli')
          .slice(0, 6)

        state.newProducts = newProducts
        state.coffeeProducts = coffeeProducts
        state.dripProducts = dripProducts
        state.capsuleProducts = capsuleProducts
        state.loadingNew = false
        state.loadingCoffee = false
        state.loadingDrip = false
        state.loadingCapsule = false
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch products'
      })

    // Fetch product by slug
    builder
      .addCase(fetchProductBySlug.pending, state => {
        state.loadingProduct = true
        state.error = null
      })
      .addCase(fetchProductBySlug.fulfilled, (state, action) => {
        state.loadingProduct = false
        state.product = action.payload
        state.lastProductFetch = Date.now()
      })
      .addCase(fetchProductBySlug.rejected, (state, action) => {
        state.loadingProduct = false
        state.error = action.error.message || 'Failed to fetch product'
      })

    // Fetch related products
    builder
      .addCase(fetchRelatedProducts.pending, state => {
        state.loadingRelated = true
      })
      .addCase(fetchRelatedProducts.fulfilled, (state, action) => {
        state.loadingRelated = false
        state.relatedProducts = action.payload
      })
      .addCase(fetchRelatedProducts.rejected, state => {
        state.loadingRelated = false
      })
  },
})

export const { filterProducts, sortProducts, clearProduct, clearError, setCategorizedProducts } =
  productsSlice.actions

export default productsSlice.reducer
